/**
 * GameDataContext
 *
 * Fetches all static game data from the backend and provides it to the entire application.
 * This is the single source of truth for game configuration, Pokemon data, moves, etc.
 *
 * Data is fetched once on app load and cached. Components can use the useGameData hook
 * to access any piece of game data.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { setGameData as setStoreGameData } from '../shared/gameDataStore';

// API Configuration - ALWAYS use production URL
// NEVER use localhost - this is a failsafe to prevent accidental local API calls
const API_URL = 'https://pokesume-backend-321590971328.us-west1.run.app/api';

const GameDataContext = createContext(null);

/**
 * Hook to access game data
 * @returns {Object} All game data and loading state
 */
export const useGameData = () => {
  const context = useContext(GameDataContext);
  if (!context) {
    throw new Error('useGameData must be used within GameDataProvider');
  }
  return context;
};

/**
 * Normalize support name helper function
 * Recreated here since we can't import from the backend
 */
const createNormalizeSupportName = (LEGACY_SUPPORT_NAME_MAP) => (name) => {
  if (!name) return name;
  return LEGACY_SUPPORT_NAME_MAP[name] || name;
};

/**
 * Get support at limit break helper function
 * Recreated here to match backend logic in supports.js
 */
const createGetSupportAtLimitBreak = (SUPPORT_CARDS, SUPPORT_LIMIT_BREAK_PROGRESSIONS, normalizeNameFn) => (supportName, limitBreakLevel = 4) => {
  const normalizedName = normalizeNameFn ? normalizeNameFn(supportName) : supportName;
  const baseCard = SUPPORT_CARDS[normalizedName];
  if (!baseCard) return null;

  // Clamp limit break level
  const lb = Math.max(0, Math.min(4, limitBreakLevel));

  // If at max LB or no progression defined, return base card
  const progression = SUPPORT_LIMIT_BREAK_PROGRESSIONS[normalizedName];
  if (lb === 4 || !progression) return baseCard;

  const lbData = progression.progression?.[lb];
  if (!lbData) return baseCard;

  // Apply multipliers to base stats
  const adjustedBaseStats = {};
  if (baseCard.baseStats) {
    for (const [stat, value] of Object.entries(baseCard.baseStats)) {
      adjustedBaseStats[stat] = Math.floor(value * lbData.baseStats);
    }
  }

  // Apply multipliers to training bonuses
  const adjustedTrainingBonus = baseCard.trainingBonus ? {
    typeMatch: Math.floor(baseCard.trainingBonus.typeMatch * lbData.trainingBonus),
    otherStats: Math.max(1, Math.floor(baseCard.trainingBonus.otherStats * lbData.trainingBonus)),
    maxFriendshipTypeMatch: Math.floor(baseCard.trainingBonus.maxFriendshipTypeMatch * lbData.trainingBonus)
  } : null;

  // Handle special effects
  let adjustedSpecialEffect = null;
  if (baseCard.specialEffect && lbData.specialEffect > 0) {
    adjustedSpecialEffect = {};
    const integerBonusEffects = ['maxEnergyBonus', 'restBonus', 'friendshipGainBonus', 'energyRegenBonus', 'energyCostReduction'];

    for (const [effect, value] of Object.entries(baseCard.specialEffect)) {
      if (typeof value === 'number') {
        if (effect.includes('Multiplier')) {
          const bonus = value - 1.0;
          adjustedSpecialEffect[effect] = 1.0 + (bonus * lbData.specialEffect);
        } else if (integerBonusEffects.includes(effect)) {
          adjustedSpecialEffect[effect] = Math.round(value * lbData.specialEffect);
        } else {
          adjustedSpecialEffect[effect] = value * lbData.specialEffect;
        }
      } else {
        adjustedSpecialEffect[effect] = value;
      }
    }

    // Check if any effect is meaningful
    const hasSignificantEffect = Object.entries(adjustedSpecialEffect).some(([key, val]) => {
      if (key.includes('Multiplier')) return val > 1.005;
      if (integerBonusEffects.includes(key)) return val > 0;
      return val > 0.001;
    });
    if (!hasSignificantEffect) adjustedSpecialEffect = null;
  }

  return {
    ...baseCard,
    baseStats: adjustedBaseStats,
    trainingBonus: adjustedTrainingBonus,
    appearanceRate: lbData.appearanceRate,
    typeMatchPreference: lbData.typeMatchPreference,
    initialFriendship: lbData.initialFriendship,
    specialEffect: adjustedSpecialEffect,
    _limitBreakLevel: lb,
    _isMaxLimitBreak: false
  };
};

/**
 * Calculate base stats helper function
 * Recreated here since we can't import from the backend
 */
const createCalculateBaseStats = (EVOLUTION_CONFIG) => (rawStats, evolutionStages) => {
  let multiplier = EVOLUTION_CONFIG.BASE_STAT_MULTIPLIERS.TWO_EVOLUTIONS;

  if (evolutionStages === 0) {
    multiplier = EVOLUTION_CONFIG.BASE_STAT_MULTIPLIERS.NO_EVOLUTION;
  } else if (evolutionStages === 1) {
    multiplier = EVOLUTION_CONFIG.BASE_STAT_MULTIPLIERS.ONE_EVOLUTION;
  }

  const adjustedStats = {};
  for (const [stat, value] of Object.entries(rawStats)) {
    adjustedStats[stat] = Math.round(value * multiplier);
  }

  const total = Object.values(adjustedStats).reduce((sum, val) => sum + val, 0);
  if (total < 300) {
    const scale = 300 / total;
    for (const stat in adjustedStats) {
      adjustedStats[stat] = Math.round(adjustedStats[stat] * scale);
    }
  } else if (total > 400) {
    const scale = 400 / total;
    for (const stat in adjustedStats) {
      adjustedStats[stat] = Math.round(adjustedStats[stat] * scale);
    }
  }

  return adjustedStats;
};

// Cache key for localStorage
const GAMEDATA_CACHE_KEY = 'pokesume_gamedata_cache';
const GAMEDATA_ETAG_KEY = 'pokesume_gamedata_etag';

export const GameDataProvider = ({ children }) => {
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch game data from backend
  const fetchGameData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check for cached data
      const cachedEtag = localStorage.getItem(GAMEDATA_ETAG_KEY);

      const headers = {};
      if (cachedEtag) {
        headers['If-None-Match'] = cachedEtag;
      }

      console.log('[GameData] Fetching game data from:', `${API_URL}/gamedata`);
      const response = await fetch(`${API_URL}/gamedata`, {
        method: 'GET',
        headers,
        mode: 'cors'
      });

      // If 304 Not Modified, use cached data
      if (response.status === 304) {
        console.log('[GameData] Using cached data (304 Not Modified)');
        const cachedData = localStorage.getItem(GAMEDATA_CACHE_KEY);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          setGameData(parsed);
          setStoreGameData(parsed); // Also update the synchronous store
          setLoading(false);
          return;
        }
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch game data: ${response.status}`);
      }

      const data = await response.json();
      console.log('[GameData] Received game data:', Object.keys(data));

      // Cache the data
      const etag = response.headers.get('ETag');
      if (etag) {
        localStorage.setItem(GAMEDATA_ETAG_KEY, etag);
      }
      localStorage.setItem(GAMEDATA_CACHE_KEY, JSON.stringify(data));

      setGameData(data);
      setStoreGameData(data); // Also update the synchronous store
    } catch (err) {
      console.error('[GameData] Error fetching game data:', err);
      setError(err.message);

      // Try to use cached data as fallback
      const cachedData = localStorage.getItem(GAMEDATA_CACHE_KEY);
      if (cachedData) {
        console.log('[GameData] Using cached data as fallback');
        try {
          const parsed = JSON.parse(cachedData);
          setGameData(parsed);
          setStoreGameData(parsed); // Also update the synchronous store
        } catch (parseErr) {
          console.error('[GameData] Failed to parse cached data:', parseErr);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  // Create helper functions that depend on the loaded data
  const helpers = useMemo(() => {
    if (!gameData) return {};

    const normalizeFn = createNormalizeSupportName(gameData.LEGACY_SUPPORT_NAME_MAP || {});

    return {
      normalizeSupportName: normalizeFn,
      getSupportAtLimitBreak: createGetSupportAtLimitBreak(
        gameData.SUPPORT_CARDS || {},
        gameData.SUPPORT_LIMIT_BREAK_PROGRESSIONS || {},
        normalizeFn
      ),
      calculateBaseStats: createCalculateBaseStats(gameData.EVOLUTION_CONFIG || {})
    };
  }, [gameData]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Loading state
    loading,
    error,
    refetch: fetchGameData,

    // All raw data
    ...gameData,

    // Helper functions
    ...helpers,

    // Convenience getters for commonly used data
    isReady: !loading && gameData !== null
  }), [loading, error, fetchGameData, gameData, helpers]);

  return (
    <GameDataContext.Provider value={value}>
      {children}
    </GameDataContext.Provider>
  );
};

export default GameDataContext;
